import React, { useCallback, useMemo, useState } from 'react';
import { Clock, AlertTriangle, ArrowDown, ArrowUp, ArrowUpDown, User, ChevronRight } from 'lucide-react';
import type { TaskListItem } from '../../shared/types/task';

interface GestaoListViewProps {
  tasks: TaskListItem[];
  onTaskClick: (taskId: string) => void;
  isLoading: boolean;
}

type SortKey = 'ref' | 'stage' | 'instance' | 'status' | 'assignee' | 'sla' | 'priority';
type SortDir = 'asc' | 'desc';

const COLUMNS: ReadonlyArray<{ key: SortKey | null; label: string; className?: string }> = [
  { key: 'ref', label: 'Ref' },
  { key: 'stage', label: 'Etapa' },
  { key: 'instance', label: 'Instancia' },
  { key: 'status', label: 'Status' },
  { key: 'assignee', label: 'Responsavel' },
  { key: 'sla', label: 'SLA' },
  { key: 'priority', label: 'Prioridade' },
  { key: null, label: '', className: 'w-8' },
];

const STATUS_RANK: Record<string, number> = {
  in_progress: 6,
  pending: 5,
  skipped: 4,
  failed: 3,
  cancelled: 2,
  completed: 1,
};

const PRIORITY_RANK: Record<string, number> = { critica: 4, alta: 3, media: 2, baixa: 1 };

const statusLabel: Record<string, string> = {
  pending: 'Pendente',
  in_progress: 'Em Andamento',
  completed: 'Concluida',
  failed: 'Falhou',
  skipped: 'Pulada',
  cancelled: 'Cancelada',
};

const statusColor: Record<string, string> = {
  pending: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  skipped: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  cancelled: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-500',
};

export const GestaoListView: React.FC<GestaoListViewProps> = ({
  tasks,
  onTaskClick,
  isLoading,
}) => {
  const [sortKey, setSortKey] = useState<SortKey>('sla');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const handleSortChange = useCallback((key: SortKey) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      const textual: SortKey[] = ['ref', 'stage', 'instance', 'assignee'];
      setSortDir(textual.includes(key) ? 'asc' : 'desc');
      return key;
    });
  }, []);

  const sortedTasks = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const valueRef = (t: TaskListItem) => (t.referenceCode ?? t.instanceId ?? '').toString();
    const cmp = (a: TaskListItem, b: TaskListItem): number => {
      switch (sortKey) {
        case 'ref':
          return valueRef(a).localeCompare(valueRef(b)) * dir;
        case 'stage':
          return (a.stageName ?? '').localeCompare(b.stageName ?? '') * dir;
        case 'instance':
          return (a.instanceTitle ?? '').localeCompare(b.instanceTitle ?? '') * dir;
        case 'status':
          return ((STATUS_RANK[a.status] ?? 0) - (STATUS_RANK[b.status] ?? 0)) * dir;
        case 'assignee':
          return ((a.assignedToName ?? '\uffff').localeCompare(b.assignedToName ?? '\uffff')) * dir;
        case 'sla': {
          const aHas = !!a.slaDeadline;
          const bHas = !!b.slaDeadline;
          if (aHas !== bHas) return aHas ? -1 : 1;
          if (!aHas) return 0;
          return (new Date(a.slaDeadline!).getTime() - new Date(b.slaDeadline!).getTime()) * dir;
        }
        case 'priority':
          return ((PRIORITY_RANK[(a.priority ?? '').toLowerCase()] ?? 0) - (PRIORITY_RANK[(b.priority ?? '').toLowerCase()] ?? 0)) * dir;
        default:
          return 0;
      }
    };
    return [...tasks].sort(cmp);
  }, [tasks, sortKey, sortDir]);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center text-slate-400 dark:text-slate-500">
        <div className="animate-pulse text-sm">Carregando...</div>
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-slate-400 dark:text-slate-500 text-sm">
        Nenhuma tarefa encontrada com os filtros atuais.
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs font-semibold uppercase text-slate-400 dark:text-slate-500 border-b border-slate-200 dark:border-slate-800">
            {COLUMNS.map((col, idx) => {
              if (!col.key) {
                return <th key={`spacer-${idx}`} className={`px-4 py-3 ${col.className ?? ''}`} />;
              }
              const isActive = sortKey === col.key;
              const Indicator = isActive ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
              return (
                <th key={col.key} className={`px-4 py-3 ${col.className ?? ''}`}>
                  <button
                    type="button"
                    onClick={() => handleSortChange(col.key as SortKey)}
                    className={`group inline-flex items-center gap-1 uppercase tracking-wide transition-colors ${
                      isActive
                        ? 'text-slate-700 dark:text-slate-300'
                        : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'
                    }`}
                    title={`Ordenar por ${col.label}`}
                  >
                    <span>{col.label}</span>
                    <Indicator size={10} className={isActive ? 'opacity-100' : 'opacity-40 group-hover:opacity-70'} />
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedTasks.map((task) => (
            <tr
              key={task.id}
              onClick={() => onTaskClick(task.id)}
              className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors"
            >
              <td className="px-4 py-3 font-mono text-xs text-slate-400">
                {task.referenceCode ?? task.instanceId?.slice(0, 8)}
              </td>
              <td className="px-4 py-3 font-medium text-slate-700 dark:text-slate-300">
                {task.stageName}
              </td>
              <td className="px-4 py-3 text-slate-500 dark:text-slate-400 truncate max-w-[200px]">
                {task.instanceTitle ?? '-'}
              </td>
              <td className="px-4 py-3">
                <span className={`text-[10px] font-semibold px-2 py-1 rounded-sm ${statusColor[task.status] ?? statusColor.pending}`}>
                  {statusLabel[task.status] ?? task.status}
                </span>
              </td>
              <td className="px-4 py-3 text-slate-500 dark:text-slate-400">
                <div className="flex items-center gap-1">
                  <User size={12} />
                  <span className="truncate max-w-[100px]">
                    {task.assignedToName ?? task.assignedTo?.slice(0, 8) ?? '-'}
                  </span>
                </div>
              </td>
              <td className="px-4 py-3">
                {task.slaDeadline ? (
                  <div className={`flex items-center gap-1 text-xs ${task.isOverdue ? 'text-red-600 dark:text-red-400' : 'text-slate-400 dark:text-slate-500'}`}>
                    {task.isOverdue && <AlertTriangle size={10} />}
                    <Clock size={10} />
                    {new Date(task.slaDeadline).toLocaleDateString('pt-BR')}
                  </div>
                ) : (
                  <span className="text-slate-300 dark:text-slate-600">-</span>
                )}
              </td>
              <td className="px-4 py-3">
                <span className="text-xs uppercase font-semibold text-slate-500 dark:text-slate-400">
                  {task.priority ?? '-'}
                </span>
              </td>
              <td className="px-4 py-3">
                <ChevronRight size={14} className="text-slate-300 dark:text-slate-600" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
